/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Angular-native application event delivery.
 */
import {Injectable, NgZone} from '@angular/core';

export interface AppEvent {
  readonly name: string;
  readonly targetScope: AppEventBusService;
  currentScope: AppEventBusService | null;
  defaultPrevented: boolean;
  preventDefault(): void;
}

type AppEventListener = (event: AppEvent, ...args: any[]) => void;

@Injectable({
  providedIn: 'root'
})
export class AppEventBusService {
  private readonly listeners = new Map<string, Set<AppEventListener>>();

  constructor(private readonly zone: NgZone) {
  }

  on(name: string, listener: AppEventListener): () => void {
    let eventListeners = this.listeners.get(name);
    if (!eventListeners) {
      eventListeners = new Set<AppEventListener>();
      this.listeners.set(name, eventListeners);
    }
    eventListeners.add(listener);

    return () => {
      eventListeners?.delete(listener);
      if (eventListeners?.size === 0) {
        this.listeners.delete(name);
      }
    };
  }

  emit(name: string, ...args: any[]): AppEvent {
    return this.dispatch(name, args);
  }

  run<T>(work?: () => T): T | undefined {
    return this.zone.run(() => work ? work() : undefined);
  }

  runAsync(work?: () => void): void {
    Promise.resolve().then(() => {
      this.zone.run(() => {
        if (work) {
          work();
        }
      });
    });
  }

  private dispatch(name: string, args: any[]): AppEvent {
    const event: AppEvent = {
      name,
      targetScope: this,
      currentScope: this,
      defaultPrevented: false,
      preventDefault(): void {
        this.defaultPrevented = true;
      }
    };

    const eventListeners = this.listeners.get(name);
    if (eventListeners) {
      [...eventListeners].forEach((listener) => listener(event, ...args));
    }
    event.currentScope = null;
    return event;
  }
}
